import React from "react";
import styled from "styled-components";
import Popup from "../Popup";
import ConfirmDialog from "../ConfirmDialog";
import UserAccount from "../../services/UserAccount";
import LogictryComponent from "../../submodules/logictry_config/LogictryComponent";
import Constants from "../../submodules/logictry_config/constants";

import {
  Card,
  LogoHeader,
  Title,
  Subtitle,
  PrimaryButton,
  SecondaryTextButton,
  CheckboxRow,
  WarningText,
} from "../../styledhtml/Tailwind";

//
// Additional minimal wrappers styled to match your system
//

const LegalSection = styled.div`
  margin-top: 1.25rem;
  margin-bottom: 1.25rem;
  font-size: ${Constants.SmallFontSize};
  line-height: 1.55;
`;

const LegalList = styled.ul`
  list-style-type: disc;
  margin-top: 0.75rem;
  margin-bottom: 0.75rem;
  padding-left: 1.25rem;

  li {
    margin-bottom: 0.35rem;

    a {
      color: ${Constants.PrimaryColor};
      text-decoration: underline;

      &:hover {
        opacity: 0.8;
      }
    }
  }
`;

const CenterText = styled.p`
  text-align: center;
  font-size: ${Constants.SmallFontSize};
  color: ${Constants.MediumTextGray};
  margin-top: 1rem;
`;

export default class LegalPopup extends LogictryComponent {
  state = {
    agreed: false,
    liveActivityEnabled: null,
    confirmDisableLiveActivity: false,
    showError: false,
  };

  componentDidMount() {
    UserAccount.onStateUpdate(this);
  }

  componentWillUnmount() {
    UserAccount.offStateUpdate(this);
  }

  accept = () => {
    const { agreed, liveActivityEnabled } = this.state;
    if (!agreed) return this.setState({ showError: true });
    if (liveActivityEnabled === null) return this.setState({ showError: true });

    const { account } = UserAccount;

    UserAccount.updateUserProfile({
      _id: account._id,
      privacySettings: {
        ...(account.privacySettings || {}),
        disableLiveActivity: !liveActivityEnabled,
      },
      legal: {
        terms: new Date(),
        privacy: new Date(),
        creator: new Date(),
        dpa: new Date(),
      },
    });
  };

  chooseLiveActivity = (enabled) => {
    if (enabled) {
      this.setState({ liveActivityEnabled: true, showError: false });
      return;
    }
    this.setState({ confirmDisableLiveActivity: true });
  };

  render() {
    if (!UserAccount.isLoggedIn) return null;

    const { account } = UserAccount;
    const { legal } = account;

    const { agreed, showError, confirmDisableLiveActivity, liveActivityEnabled } = this.state;

    // If already accepted, do not show
    if (legal?.terms && legal?.privacy && legal?.creator && legal?.dpa) return null;
    // Deferred to 1.98.0: re-enable the legal and Live activity choice popup.
    return null;

    return (
      <Popup preventDeletion>
        <Card
          onClick={(e) => e.stopPropagation()}
          style={{ animation: "fadeIn 0.25s ease-out" }}
        >
          <LogoHeader>
            <img src={Constants.Logo} alt="Logictry" />
          </LogoHeader>

          <Title>Welcome to Logictry</Title>

          <Subtitle>
            Logictry helps experts turn their knowledge into interactive,
            logic-powered tools that help people learn faster and make better
            decisions.
          </Subtitle>

          <LegalSection>
            <p>
              Before continuing, please review and accept our core platform
              agreements:
            </p>
            <p>
              Logictry uses a first-party sign-in cookie to keep you signed in. That cookie
              is separate from Live activity. Choosing Live activity off does not turn off
              sign-in or other cookies the service needs to run.
            </p>
            <p id="live-activity-choice">
              Choose Live activity before you continue. You can use Logictry either way, and
              you can change this later in Privacy Settings.
            </p>
            <p>
              <strong>Live Activity On.</strong> Your signed-in visits may appear in live viewer
              lists and in daily viewer analytics. Eligible people can then see visitor
              information for content or collections they own or administer, under the existing
              role rules. Daily viewer analytics cover the most recent 90 days and are limited
              to approved Logic Circle creators with the required owner, admin, or editor role
              who also have Live activity on.
            </p>
            <p>
              <strong>Live Activity Off.</strong> Your future visits are not recorded for viewer
              analytics, and your profile is hidden from live viewer lists. You also cannot see
              live viewer information or open daily viewer analytics. Records already collected
              while Live activity was on are kept indefinitely under the Privacy Policy. The
              analytics screens still show only the most recent 90 days of those records.
            </p>

            <LegalList>
              <li>
                <a
                  href="https://logictry.com/legal/terms"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Terms of Service
                </a>
              </li>
              <li>
                <a
                  href="https://logictry.com/legal/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Privacy Policy
                </a>
              </li>
              <li>
                <a
                  href="https://logictry.com/legal/creator"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Creator Agreement
                </a>
              </li>
              <li>
                <a
                  href="https://logictry.com/legal/dpa"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Data Processing Addendum (DPA)
                </a>
              </li>
            </LegalList>
            <p>
              These documents govern your use of Logictry, including interacting with
              content, creating content, and building or subscribing to collections.
            </p>
          </LegalSection>

          <div
            role="radiogroup"
            aria-labelledby="live-activity-choice"
            style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap", margin: "1rem 0" }}
          >
            <PrimaryButton
              type="button"
              role="radio"
              aria-checked={liveActivityEnabled === true}
              onClick={() => this.chooseLiveActivity(true)}
              style={{ flex: "1 1 12rem", opacity: liveActivityEnabled === false ? 0.75 : 1 }}
            >
              Live Activity On
            </PrimaryButton>
            <PrimaryButton
              type="button"
              role="radio"
              aria-checked={liveActivityEnabled === false}
              onClick={() => this.chooseLiveActivity(false)}
              style={{ flex: "1 1 12rem", opacity: liveActivityEnabled === true ? 0.75 : 1 }}
            >
              Live Activity Off
            </PrimaryButton>
          </div>

          <CheckboxRow>
            <input
              type="checkbox"
              checked={agreed}
              onChange={() => this.setState({ agreed: !agreed, showError: false })}
            />
            <span>
              I agree to the Terms of Service, Privacy Policy, Creator Agreement,
              and DPA.
            </span>
          </CheckboxRow>

          <PrimaryButton
            style={{ marginTop: "1.5rem" }}
            onClick={this.accept}
          >
            Continue
          </PrimaryButton>

          {showError && <WarningText>Please choose a Live activity option and check the box to continue.</WarningText>}

          <CenterText>
            <SecondaryTextButton onClick={() => UserAccount.logout()}>
              I don’t agree
            </SecondaryTextButton>
          </CenterText>

          {confirmDisableLiveActivity && (
            <ConfirmDialog
              text="Continue with Live activity off?"
              description="With Live activity off, you won't see live viewers or daily viewer analytics for Content and Collections, and your future views won't be recorded for viewer analytics. Your profile also won't appear in live viewer lists. Historical viewer records collected while Live activity was on are retained indefinitely under the Privacy Policy. You can turn it on later in Privacy Settings."
              open
              onNo={() => this.setState({ confirmDisableLiveActivity: false })}
              onYes={() => this.setState({
                confirmDisableLiveActivity: false,
                liveActivityEnabled: false,
                showError: false,
              })}
            />
          )}

        </Card>
      </Popup>
    );
  }
}
